import { Group } from '@mantine/core';

import { TSDData } from '@e-origin/contracts';
import { TSDBusinessValidationType, TSDStatus, TSDType } from '@e-origin/shared';

import { TableNotificationIndicator } from '@components';
import { canEditTsdAfterPresentationNotificationFailure, countCustomsStateNotifications } from '../utils';
import { TSDViewAuditView } from './TSDViewAuditView';
import { TSDViewConsignment } from './TSDViewConsignment';
import { TSDViewCustoms } from './TSDViewCustoms';
import { TSDViewGeneralInfo } from './TSDViewGeneralInfo';
import { TSDViewPNData } from './TSDViewPNData';

export interface TSDViewTabConfig {
  value: string;
  label: React.ReactNode;
  panel: React.ReactNode | null;
}

interface GetTsdVisibleTabsProps {
  tsd: TSDData;
  disabled: boolean;
}

const customsStateNotificationIcon = (transit): React.ReactNode => {
  const { notificationsPending } = countCustomsStateNotifications(transit);

  return <TableNotificationIndicator notificationsPending={notificationsPending} />;
};

export const getTSDVisibleTabs = ({ tsd, disabled }: GetTsdVisibleTabsProps): TSDViewTabConfig[] => {
  const isPrelodgedOrCombined = [TSDType.PRELODGED, TSDType.COMBINED].includes(tsd.generalInfo.type);
  const generalInfoDisabled = tsd.generalInfo?.isAmended ? false : disabled;

  const consignmentDisabled =
    tsd.generalInfo?.isAmended ||
    (tsd.generalInfo?.status === TSDStatus.ACCEPTED && !tsd.generalInfo?.isDeconsolidated) ||
    (tsd.generalInfo.status === TSDStatus.REJECTED && tsd.generalInfo.isDeconsolidatedRejected)
      ? false
      : disabled;

  const pnDataDisabled = canEditTsdAfterPresentationNotificationFailure({
    status: tsd.generalInfo?.status,
    businessValidationType: tsd.customsState?.businessValidationType as TSDBusinessValidationType,
  })
    ? false
    : disabled;

  return [
    {
      value: 'general-info',
      label: 'General info',
      panel: <TSDViewGeneralInfo tsd={tsd} disabled={generalInfoDisabled} />,
    },
    {
      value: 'consignments',
      label: 'Consignments',
      panel: <TSDViewConsignment tsd={tsd} disabled={consignmentDisabled} />,
    },
    ...(isPrelodgedOrCombined
      ? [
          {
            value: 'pn-data',
            label: 'PN Data',
            panel: <TSDViewPNData tsd={tsd} disabled={pnDataDisabled} />,
          },
        ]
      : []),
    {
      value: 'customs-state',
      label: <Group wrap="nowrap">Customs State {customsStateNotificationIcon(tsd)}</Group>,
      panel: <TSDViewCustoms tsd={tsd} />,
    },
    {
      value: 'audit-view',
      label: 'Audit View',
      panel: <TSDViewAuditView tsd={tsd} />,
    },
  ];
};
